import { Activity, TrendingUp, Wallet, FileCheck2 } from "lucide-react";

const ROWS = [
    { id: "TXN-9182", who: "Acme Imports LLC", ccy: "USD", amt: "$10,000", status: "INR Credited", tone: "ok" },
    { id: "TXN-9181", who: "Stripe Payouts", ccy: "USD", amt: "$4,200", status: "FIRA Generated", tone: "ok" },
    { id: "TXN-9180", who: "Hyperlocal Ltd", ccy: "GBP", amt: "£12,000", status: "Compliance Check", tone: "warn" },
    { id: "TXN-9179", who: "Cloudbase Inc", ccy: "USD", amt: "$2,800", status: "Sent", tone: "ok" },
    { id: "TXN-9178", who: "Northwind FZE", ccy: "AED", amt: "د.إ 18,500", status: "INR Credited", tone: "ok" },
];

export default function DashboardPreview() {
    return (
        <section
            data-testid="dashboard-preview-section"
            className="relative bg-[#04110D] text-white py-24 md:py-32 px-4 md:px-8 overflow-hidden"
        >
            <div className="absolute inset-0 bg-grid-emerald opacity-50" aria-hidden />
            <div
                aria-hidden
                className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[480px] w-[1000px] rounded-full bg-emerald-500/15 blur-[120px]"
            />
            <div className="relative mx-auto max-w-7xl">
                <div className="max-w-2xl">
                    <span className="text-xs tracking-[0.25em] uppercase font-bold text-emerald-300">
                        Dashboard preview
                    </span>
                    <h2 className="font-heading mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                        Every cross-border ops question, answered in one screen.
                    </h2>
                </div>

                <div className="mt-12 rounded-3xl border border-white/10 bg-zinc-950/60 backdrop-blur-xl p-4 sm:p-6 lg:p-8">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-emerald-500/20 ring-1 ring-emerald-400/40 grid place-items-center">
                                <Wallet className="h-4 w-4 text-emerald-300" />
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Workspace</div>
                                <div className="text-sm font-bold">Truopay · Acme Pvt Ltd</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 font-mono text-xs text-zinc-400">
                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-flow-pulse" />
                            Live data · {new Date().toLocaleDateString()}
                        </div>
                    </div>

                    {/* KPI strip */}
                    <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {[
                            { k: "FX Savings (30d)", v: "₹18.4L", sub: "+22% vs banks", icon: TrendingUp },
                            { k: "Volume (30d)", v: "$1.42M", sub: "↑ 38% MoM", icon: Activity },
                            { k: "Avg Settle", v: "21h 48m", sub: "<24h SLA", icon: Wallet },
                            { k: "FIRC Issued", v: "126", sub: "100% auto", icon: FileCheck2 },
                        ].map((k) => (
                            <div
                                key={k.k}
                                className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] tracking-widest uppercase text-zinc-400 font-bold">
                                        {k.k}
                                    </span>
                                    <k.icon className="h-4 w-4 text-emerald-300" />
                                </div>
                                <div className="mt-2 font-mono text-2xl font-extrabold">{k.v}</div>
                                <div className="text-xs text-emerald-300 mt-0.5">{k.sub}</div>
                            </div>
                        ))}
                    </div>

                    {/* Main grid */}
                    <div className="mt-6 grid lg:grid-cols-3 gap-4 sm:gap-6">
                        {/* Chart */}
                        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">
                                        FX savings · last 12 weeks
                                    </div>
                                    <div className="mt-1 font-heading text-2xl font-extrabold font-mono">
                                        ₹46,82,400
                                    </div>
                                </div>
                                <span className="text-xs font-bold rounded-full bg-emerald-500/15 text-emerald-300 px-2.5 py-1">
                                    ↑ 31%
                                </span>
                            </div>
                            <div className="mt-6 flex items-end gap-2 h-44 sm:h-56">
                                {[40, 60, 55, 70, 65, 85, 75, 92, 80, 110, 95, 130].map((h, i) => (
                                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                        <div
                                            className="w-full rounded-md bg-gradient-to-t from-emerald-700 to-emerald-300"
                                            style={{ height: `${h}px` }}
                                        />
                                        <span className="text-[9px] text-zinc-500 font-mono">
                                            W{i + 1}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Balances */}
                        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6">
                            <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">
                                Multi-currency balances
                            </div>
                            <div className="mt-4 space-y-3">
                                {[
                                    { c: "USD", a: "128,540.20", f: "🇺🇸" },
                                    { c: "GBP", a: "42,310.00", f: "🇬🇧" },
                                    { c: "CAD", a: "18,422.50", f: "🇨🇦" },
                                    { c: "AED", a: "92,140.00", f: "🇦🇪" },
                                    { c: "INR", a: "1,06,82,400", f: "🇮🇳" },
                                ].map((b) => (
                                    <div
                                        key={b.c}
                                        className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5"
                                    >
                                        <span className="flex items-center gap-2 text-sm">
                                            <span>{b.f}</span>
                                            <span className="font-mono text-zinc-300">{b.c}</span>
                                        </span>
                                        <span className="font-mono text-sm font-semibold">
                                            {b.a}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Transactions table */}
                    <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
                        <div className="px-5 sm:px-6 py-4 border-b border-white/10 flex items-center justify-between">
                            <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">
                                Recent transactions
                            </div>
                            <span className="text-xs font-mono text-zinc-500">
                                showing 5 of 1,284
                            </span>
                        </div>
                        <div className="divide-y divide-white/5">
                            {ROWS.map((r) => (
                                <div
                                    key={r.id}
                                    className="grid grid-cols-12 items-center px-5 sm:px-6 py-3.5 text-sm hover:bg-white/[0.02]"
                                >
                                    <div className="col-span-3 sm:col-span-2 font-mono text-xs text-zinc-400 truncate">
                                        {r.id}
                                    </div>
                                    <div className="col-span-5 sm:col-span-5 truncate font-medium">
                                        {r.who}
                                    </div>
                                    <div className="col-span-4 sm:col-span-2 font-mono text-right sm:text-left">
                                        {r.amt}
                                    </div>
                                    <div className="hidden sm:flex sm:col-span-3 justify-end">
                                        <span
                                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                                                r.tone === "ok"
                                                    ? "bg-emerald-500/15 text-emerald-300"
                                                    : "bg-amber-400/15 text-amber-300"
                                            }`}
                                        >
                                            <span
                                                className={`h-1.5 w-1.5 rounded-full ${
                                                    r.tone === "ok"
                                                        ? "bg-emerald-400"
                                                        : "bg-amber-400 animate-flow-pulse"
                                                }`}
                                            />
                                            {r.status}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
